import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  employeeApiRequest,
  getEmployeeToken,
  getStoredEmployeeProfile,
  updateStoredEmployeeProfile,
  clearEmployeeSession,
} from '../utils/employeeApi';
import { API_BASE_URL } from '../utils/api';
import { resolveFileUrl } from '../utils/fileUrl';
import {
  FiUser,
  FiFileText,
  FiBell,
  FiMessageCircle,
  FiChevronRight,
  FiDownload,
  FiClipboard,
  FiLogOut,
} from 'react-icons/fi';
import brandLogo from '../assets/images/logo.png';
import './EmployeeMobile.css';


const ALLOWED_DOCUMENT_TYPES = [
  'employee photo',
  'aadhaar card',
  'aadhar card',
  'pan card',
  'bank passbook',
  'driving license',
  'driving licence',
];

const resolveUploadImageUrl = resolveFileUrl;
const appendVersionParam = (url, version) => {
  if (!url) return null;
  if (!version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(version))}`;
};

const normalizeDocumentType = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isDocumentAllowed = (document) => {
  const normalizedType = normalizeDocumentType(document?.documentType);
  if (normalizedType && ALLOWED_DOCUMENT_TYPES.some((type) => normalizedType.includes(type))) {
    return true;
  }

  const normalizedName = normalizeDocumentType(document?.fileName);
  return normalizedName && ALLOWED_DOCUMENT_TYPES.some((type) => normalizedName.includes(type));
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShortDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
};

const parseAmount = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = String(value).replace(/,/g, '').trim();
  if (!raw) {
    return null;
  }

  const numeric = Number.parseFloat(raw);
  return Number.isNaN(numeric) ? null : numeric;
};

const formatAmount = (value) => {
  const amount = parseAmount(value);
  if (amount === null) {
    return '—';
  }

  try {
    return amount.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  } catch (error) {
    return amount.toString();
  }
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string' && value.trim() === '') {
    return '—';
  }

  return value;
};

const sanitizeFileSegment = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  const normalized = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  return normalized || fallback;
};

const formatEnumValue = (value) => {
  const displayValue = getDisplayValue(value);
  if (displayValue === '—') {
    return displayValue;
  }

  return String(displayValue)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveNotificationTitle = (notification) => {
  const candidates = [notification?.title, notification?.subject, notification?.heading];
  const title = candidates.find((value) => typeof value === 'string' && value.trim());
  return title?.trim() || 'Notification';
};

const resolveNotificationDescription = (notification) =>
  (notification?.description || notification?.message || '').trim() || '—';

const resolveCurrentDesignationValue = (profile = {}, isProfileApproved = false) => {
  if (!isProfileApproved) {
    return '';
  }

  const candidates = [
    profile?.currentDesignation,
    profile?.designationName,
    profile?.designation,
    profile?.finalReview?.designationName,
    profile?.finalReview?.designationLabel,
    profile?.finalReview?.designation,
  ];

  for (const candidate of candidates) {
    const value = (candidate || '').toString().trim();
    if (value) {
      return value;
    }
  }

  return '';
};

const normalizeRegistrationStatus = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return ['not_started', 'in_progress', 'submitted', 'approved'].includes(normalized)
    ? normalized
    : null;
};

const resolveRegistrationStatus = (profile, isProfileApproved) => {
  const candidates = [
    profile?.meta?.mobileRegistrationStatus,
    profile?.mobileRegistrationStatus,
    profile?.registrationStatus,
    profile?.meta?.registrationStatus,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeRegistrationStatus(candidate);
    if (normalized) {
      return normalized;
    }
  }

  if (isProfileApproved) {
    return 'approved';
  }

  return 'not_started';
};

const renderAddressValue = (value) => {
  const displayValue = getDisplayValue(value);
  if (displayValue === '—') {
    return displayValue;
  }

  return String(displayValue)
    .split(/\r?\n/)
    .filter((line, index, array) => !(array.length > 1 && line.trim() === '' && index === array.length - 1))
    .map((line, index, array) => (
      <React.Fragment key={`address-line-${index}`}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
};


const resolvePayslipLabel = (payslip) => {
  if (!payslip) {
    return '—';
  }

  if (payslip.month) {
    return payslip.month;
  }

  if (payslip.paymentDate) {
    const date = new Date(payslip.paymentDate);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
  }

  return '—';
};

const EmployeeMobileDetails = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(getStoredEmployeeProfile());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('dashboard');
  const [payslips, setPayslips] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [payslipLoading, setPayslipLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [payslipError, setPayslipError] = useState('');
  const [notificationError, setNotificationError] = useState('');
  const [enquiryError, setEnquiryError] = useState('');
  const [payslipDownloadError, setPayslipDownloadError] = useState('');
  const [payslipDownloadId, setPayslipDownloadId] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({ subject: '', message: '' });
  const [enquiryStatus, setEnquiryStatus] = useState({ success: '', error: '', submitting: false });
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);

  useEffect(() => {
    const token = getEmployeeToken();
    if (!token) {
      navigate('/employee/mobile/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await employeeApiRequest('/api/public/employee/me');
        setProfile(response.data ?? null);
        updateStoredEmployeeProfile(response.data ?? null);
      } catch (err) {
        setError(err.message || 'Failed to load your profile.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    if (!profile?.id) {
      return undefined;
    }

    let isMounted = true;

    const loadDashboardData = async () => {
      setPayslipLoading(true);
      setNotificationLoading(true);
      setEnquiryLoading(true);
      setPayslipError('');
      setPayslipDownloadError('');
      setNotificationError('');
      setEnquiryError('');

      try {
        const response = await employeeApiRequest('/api/public/employee/payslips?limit=3');
        if (isMounted) {
          setPayslips(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setPayslipError(err.message || 'Unable to load payslips');
        }
      } finally {
        if (isMounted) {
          setPayslipLoading(false);
        }
      }

      try {
        const response = await employeeApiRequest('/api/public/employee/notifications');
        if (isMounted) {
          const items = Array.isArray(response.data) ? response.data : [];
          const sortedItems = [...items].sort(
            (first, second) => new Date(second.createdAt || 0) - new Date(first.createdAt || 0),
          );
          setNotifications(sortedItems);
        }
      } catch (err) {
        if (isMounted) {
          setNotificationError(err.message || 'Unable to load notifications');
        }
      } finally {
        if (isMounted) {
          setNotificationLoading(false);
        }
      }

      try {
        const response = await employeeApiRequest('/api/public/employee/enquiries?limit=5');
        if (isMounted) {
          setEnquiries(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setEnquiryError(err.message || 'Unable to load enquiries');
        }
      } finally {
        if (isMounted) {
          setEnquiryLoading(false);
        }
      }
    };

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, [profile?.id]);

  const personalFields = useMemo(
    () => [
      { label: 'Date of Birth', value: formatDate(profile?.personal?.dob) },
      { label: 'Gender', value: formatEnumValue(profile?.personal?.gender) },
      { label: 'Marital Status', value: formatEnumValue(profile?.personal?.maritalStatus) },
      { label: "Father's Name", value: getDisplayValue(profile?.personal?.fatherName) },
      { label: 'Aadhaar Number', value: getDisplayValue(profile?.aadhar) },
      {
        label: 'Current Address',
        value: renderAddressValue(profile?.personal?.currentAddress),
      },
      {
        label: 'Permanent Address',
        value: renderAddressValue(profile?.personal?.permanentAddress),
      },
    ],
    [profile],
  );

  const bankFields = useMemo(
    () => [
      { label: 'Account Number', value: getDisplayValue(profile?.bank?.accountNumber) },
      { label: 'Bank Name', value: getDisplayValue(profile?.bank?.bankName) },
      { label: 'IFSC Code', value: getDisplayValue(profile?.bank?.ifscCode) },
      { label: 'PAN Number', value: getDisplayValue(profile?.bank?.panCard) },
      { label: 'Driving License', value: getDisplayValue(profile?.bank?.drivingLicense) },
    ],
    [profile],
  );

  const sortedNotifications = useMemo(
    () => [...notifications].sort(
      (first, second) => new Date(second.createdAt || 0) - new Date(first.createdAt || 0),
    ),
    [notifications],
  );

  const approvalStatus = (profile?.approvalStatus || profile?.status || '').toString().toLowerCase();
  const isProfileApproved = ['approved', 'active', 'verified'].includes(approvalStatus);
  const previousCompanyDesignation = (profile?.professional?.designation || '').toString().trim();
  const currentDesignation = resolveCurrentDesignationValue(profile, isProfileApproved);

  const employmentFields = useMemo(
    () => [
      { label: 'Last Company', value: getDisplayValue(profile?.professional?.lastCompany) },
      { label: 'Previous Company Designation', value: getDisplayValue(previousCompanyDesignation) },
      { label: 'Current Designation', value: getDisplayValue(currentDesignation) },
      { label: 'Experience From', value: formatDate(profile?.professional?.fromDate) },
      { label: 'Experience To', value: formatDate(profile?.professional?.toDate) },
    ],
    [currentDesignation, previousCompanyDesignation, profile],
  );

  const qualificationEntries = useMemo(() => {
    const entries = Array.isArray(profile?.education?.qualifications)
      ? profile.education.qualifications
      : [];

    return entries.map((entry, index) => {
      const titleSource =
        entry.qualificationTitle ||
        entry.qualificationName ||
        entry.qualificationType ||
        `Qualification ${index + 1}`;

      const normalizedTitle = String(titleSource || '')
        .trim()
        .toUpperCase();

      const documentUrl = resolveUploadImageUrl(
        entry.documentUrl || entry.documentPath || '',
      );

      const fields = [
        {
          label: 'Course / Specialisation',
          value: getDisplayValue(entry.courseName),
        },
        {
          label: 'Institution',
          value: getDisplayValue(entry.institutionName),
        },
        {
          label: 'Year of Passing',
          value: getDisplayValue(entry.yearOfPassing),
        },
        {
          label: 'Grade / Percentage',
          value: getDisplayValue(entry.gradeOrPercentage),
        },
      ];

      const documentLabel = getDisplayValue(entry.fileName);
      fields.push({
        label: 'Supporting Document',
        value: documentLabel,
        documentUrl,
      });

      return {
        id: entry.id || entry.localId || `qualification-${index}`,
        title: `${index + 1}. ${normalizedTitle || 'QUALIFICATION'}`,
        fields,
      };
    });
  }, [profile?.education?.qualifications]);

  const familyMembers = useMemo(
    () => (Array.isArray(profile?.family) ? profile.family : []),
    [profile?.family],
  );

  const documents = useMemo(
    () => (Array.isArray(profile?.documents) ? profile.documents : []),
    [profile?.documents],
  );

  const visibleDocuments = useMemo(
    () => documents.filter((document) => isDocumentAllowed(document)),
    [documents],
  );

  const employeePhotoUrl = useMemo(() => {
    const photoDocument = documents.find((document) => {
      const normalizedType = normalizeDocumentType(document?.documentType);
      const normalizedName = normalizeDocumentType(document?.fileName);
      return normalizedType.includes('photo') || normalizedName.includes('photo');
    });
    const photoDocumentUrl = resolveUploadImageUrl(photoDocument?.filePath || photoDocument?.fileUrl);
    const version = profile?.updatedAt || photoDocument?.updatedAt || photoDocument?.id || Date.now();
    const candidateValues = [
      profile?.photoUrl,
      profile?.photoFilePath,
      profile?.photo,
      profile?.photoName,
      photoDocumentUrl,
    ];

    for (const candidate of candidateValues) {
      const resolved = resolveUploadImageUrl(candidate);
      if (resolved) {
        return appendVersionParam(resolved, version);
      }
    }

    return null;
  }, [documents, profile?.photo, profile?.photoFilePath, profile?.photoName, profile?.photoUrl, profile?.updatedAt]);

  useEffect(() => {
    setPhotoLoadFailed(false);
  }, [employeePhotoUrl]);

  const handleDownloadPayslip = async (payslip) => {
    if (!payslip?.id || payslipDownloadId) {
      return;
    }

    setPayslipDownloadId(payslip.id);
    setPayslipDownloadError('');

    try {
      const token = getEmployeeToken();
      const response = await fetch(
        `${API_BASE_URL}/api/public/employee/payslips/${payslip.id}/pdf/modern`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (response.status === 401) {
        clearEmployeeSession(navigate);
        throw new Error('Your session has expired. Please login again.');
      }

      if (!response.ok) {
        let message = 'Failed to download payslip.';
        try {
          const text = await response.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              message = parsed?.message || parsed?.error || message;
            } catch (_error) {
              message = text;
            }
          }
        } catch (_error) {
          // ignore parsing errors
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const employeeSegment = sanitizeFileSegment(payslip.name || profile?.name, 'employee');
      const monthSegment = sanitizeFileSegment(payslip.month || resolvePayslipLabel(payslip), 'payslip');
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-new-design-${employeeSegment}-${monthSegment}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      setPayslipDownloadError(err.message || 'Unable to download payslip.');
    } finally {
      setPayslipDownloadId(null);
    }
  };

  const handleEnquiryChange = (event) => {
    const { name, value } = event.target;
    setEnquiryForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleEnquirySubmit = async (event) => {
    event.preventDefault();
    if (enquiryStatus.submitting) {
      return;
    }

    setEnquiryStatus({ success: '', error: '', submitting: true });
    try {
      const payload = {
        subject: enquiryForm.subject.trim() || undefined,
        message: enquiryForm.message.trim(),
      };

      const response = await employeeApiRequest('/api/public/employee/enquiries', {
        method: 'POST',
        body: payload,
      });

      setEnquiryStatus({ success: 'Your enquiry has been sent successfully.', error: '', submitting: false });
      setEnquiryForm({ subject: '', message: '' });
      if (response?.data) {
        setEnquiries((previous) => [response.data, ...previous].slice(0, 5));
      }
    } catch (err) {
      setEnquiryStatus({ success: '', error: err.message || 'Failed to submit enquiry.', submitting: false });
    }
  };

  const rawEmployeeStatus = (profile?.status || '').toString().trim().toLowerCase();
  const isBlockedStatus =
    ['inactive', 'relieved', 'terminated'].includes(rawEmployeeStatus) ||
    rawEmployeeStatus === '9' ||
    profile?.status === 9;
  const isPendingStatus = ['pending', 'created', 'draft'].includes(rawEmployeeStatus);

  const registrationStatus = useMemo(
    () => resolveRegistrationStatus(profile, isProfileApproved),
    [profile, isProfileApproved],
  );

  if (loading && !profile) {
    return (
      <div className="employee-mobile-page">
        <div className="employee-mobile-shell">
          <div className="employee-mobile-loading">Loading your profile…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-mobile-page">
        <div className="employee-mobile-shell">
          <div className="employee-mobile-empty" role="alert">
            <p>{error}</p>
            <button type="button" className="employee-mobile-primary" onClick={() => navigate(0)}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="employee-mobile-page">
        <div className="employee-mobile-shell">
          <div className="employee-mobile-empty">
            <p>No employee information available.</p>
            <button
              type="button"
              className="employee-mobile-primary"
              onClick={() => navigate('/employee/mobile/login', { replace: true })}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isBlockedStatus) {
    return (
      <div className="employee-mobile-page">
        <div className="employee-mobile-shell">
          <div className="employee-mobile-empty" role="alert">
            <p>Employee profile is inactive. Please contact HR.</p>
            <button type="button" className="employee-mobile-primary" onClick={() => navigate(0)}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const outletName = profile?.outlet?.name || 'Grace Super Market';
  const companyName = (
    profile?.company?.name ||
    profile?.companyName ||
    profile?.outlet?.companyName ||
    outletName ||
    ''
  ).trim();
  const employeeIdValue = getDisplayValue(profile?.permId || profile?.tempId);
  const statusText = isProfileApproved ? 'Approved' : isPendingStatus ? 'Pending Approval' : formatEnumValue(profile?.status);

  const summaryMeta = [
    { label: 'Employee ID', value: employeeIdValue },
    { label: 'Outlet', value: getDisplayValue(outletName) },
    { label: 'Company', value: getDisplayValue(companyName) },
  ];

  const shouldHideRegistrationCard = ['submitted', 'approved'].includes(registrationStatus);

  const latestEnquiry = enquiries?.[0] ?? null;
  const shouldShowPhoto = Boolean(employeePhotoUrl && !photoLoadFailed);
  const avatarAltText = profile?.name ? `${profile.name}'s photo` : 'Employee photo';

  const handleLogout = () => {
    clearEmployeeSession(navigate);
  };

  const handleLogoClick = () => {
    setActiveView('dashboard');
  };



  return (
    <div className="employee-mobile-page">
      <div className="employee-mobile-shell">
        <header className="employee-mobile-appbar">
          <button
            type="button"
            className="employee-mobile-appbar-brand"
            onClick={handleLogoClick}
            aria-label="Go to home"
          >
            <img src={brandLogo} alt="Company logo" className="employee-mobile-appbar-logo" />
            {companyName && <span className="employee-mobile-appbar-company">{companyName}</span>}
          </button>
          <button
            type="button"
            className="employee-mobile-icon-button"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <FiLogOut />
          </button>
        </header>

        {isPendingStatus && (
          <div className="employee-mobile-alert info" role="status">
            Complete your registration to finish setting up your profile.
          </div>
        )}

        {activeView === 'dashboard' && (
          <div className="employee-mobile-dashboard">
            <section className="employee-mobile-hero">
              <div className="employee-mobile-hero-top">
                {shouldShowPhoto && (
                  <div className="employee-mobile-avatar employee-mobile-avatar--image">
                    <img
                      src={employeePhotoUrl}
                      alt={avatarAltText}
                      onError={() => setPhotoLoadFailed(true)}
                    />
                  </div>
                )}
                <div className="employee-mobile-hero-text">
                  <h1>{getDisplayValue(profile?.name)}</h1>
                  <p>{currentDesignation ? getDisplayValue(currentDesignation) : 'Designation not available'}</p>
                  <span className={`employee-mobile-status-badge ${isProfileApproved ? 'approved' : 'pending'}`}>{statusText}</span>
                </div>
              </div>
              <div className="employee-mobile-hero-meta">
                {summaryMeta.map((item) => (
                  <div key={item.label}>
                    <span className="label">{item.label}</span>
                    <span className="value">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="employee-mobile-hero-contact">
                <div className="employee-mobile-hero-contact-row">
                  <div className="employee-mobile-hero-contact-item">
                    <span className="label">Contact</span>
                    <span className="value">{getDisplayValue(profile?.phone)}</span>
                  </div>
                </div>
                <div className="employee-mobile-hero-contact-item employee-mobile-hero-contact-item--full">
                  <span className="label">Email</span>
                  <span className="value">{getDisplayValue(profile?.email)}</span>
                </div>
              </div>
              <p className="employee-mobile-update-note">Last updated on {formatDateTime(profile.updatedAt)}</p>
            </section>

            <section className="employee-mobile-card-grid" aria-label="Quick links">
              <button type="button" className="employee-mobile-dashboard-card" onClick={() => setActiveView('profile')}>
                <span className="employee-mobile-card-icon profile">
                  <FiUser />
                </span>
                <div className="employee-mobile-card-body">
                  <h3>Profile info</h3>
                  <p>View your personal, bank and employment records.</p>
                </div>
                <FiChevronRight className="employee-mobile-card-arrow" />
              </button>

              {!shouldHideRegistrationCard && (
                <button
                  type="button"
                  className="employee-mobile-dashboard-card"
                  onClick={() => navigate('/employee/register')}
                >
                  <span className="employee-mobile-card-icon register">
                    <FiClipboard />
                  </span>
                  <div className="employee-mobile-card-body">
                    <h3>Employee registration</h3>
                    <p>Complete or update your records securely.</p>
                  </div>
                  <FiChevronRight className="employee-mobile-card-arrow" />
                </button>
              )}

              <button type="button" className="employee-mobile-dashboard-card" onClick={() => setActiveView('payslips')}>
                <span className="employee-mobile-card-icon payslip">
                  <FiFileText />
                </span>
                <div className="employee-mobile-card-body">
                  <h3>Monthly payslips</h3>
                  {payslipLoading ? (
                    <p className="employee-mobile-card-subtle">Loading latest payslips…</p>
                  ) : payslipError ? (
                    <p className="employee-mobile-card-error">{payslipError}</p>
                  ) : payslips.length === 0 ? (
                    <p className="employee-mobile-card-subtle">No payslips found yet.</p>
                  ) : (
                    <ul className="employee-mobile-card-list">
                      {payslips.map((payslip) => (
                        <li key={payslip.id}>
                          <span>{resolvePayslipLabel(payslip)}</span>
                          <span>{formatAmount(payslip.netPay)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <FiChevronRight className="employee-mobile-card-arrow" />
              </button>

              <button type="button" className="employee-mobile-dashboard-card" onClick={() => setActiveView('notifications')}>
                <span className="employee-mobile-card-icon notification">
                  <FiBell />
                </span>
                <div className="employee-mobile-card-body">
                  <h3>Notifications</h3>
                  {notificationLoading ? (
                    <p className="employee-mobile-card-subtle">Loading notifications…</p>
                  ) : notificationError ? (
                    <p className="employee-mobile-card-error">{notificationError}</p>
                  ) : notifications.length === 0 ? (
                    <p className="employee-mobile-card-subtle">All caught up! No new alerts.</p>
                  ) : (
                    <p className="employee-mobile-card-highlight">{notifications.length} unread message{notifications.length > 1 ? 's' : ''}</p>
                  )}
                </div>
                <FiChevronRight className="employee-mobile-card-arrow" />
              </button>


              <button type="button" className="employee-mobile-dashboard-card" onClick={() => setActiveView('enquiries')}>
                <span className="employee-mobile-card-icon enquiry">
                  <FiMessageCircle />
                </span>
                <div className="employee-mobile-card-body">
                  <h3>Send enquiry</h3>
                  {enquiryLoading ? (
                    <p className="employee-mobile-card-subtle">Preparing your help desk…</p>
                  ) : enquiryError ? (
                    <p className="employee-mobile-card-error">{enquiryError}</p>
                  ) : latestEnquiry ? (
                    <p className="employee-mobile-card-highlight">
                      Last sent on {formatDateTime(latestEnquiry.createdAt)}
                    </p>
                  ) : (
                    <p className="employee-mobile-card-subtle">Reach HR with your questions.</p>
                  )}
                </div>
                <FiChevronRight className="employee-mobile-card-arrow" />
              </button>
            </section>
          </div>
        )}

        {activeView === 'profile' && (
          <div className="employee-mobile-view">
            <header className="employee-mobile-view-header">
              <h2>Profile information</h2>
              <p>Review your verified employee details.</p>
            </header>
            <div className="employee-mobile-scroll">
              <section className="employee-mobile-summary">
                <h2>{getDisplayValue(profile.name)}</h2>
                <p>{currentDesignation ? getDisplayValue(currentDesignation) : 'Designation not available'}</p>
                <div className="employee-mobile-summary-grid">
                  <div>
                    <span className="employee-mobile-summary-label">Outlet</span>
                    <span className="employee-mobile-summary-value">{getDisplayValue(outletName)}</span>
                  </div>
                  {summaryMeta.map((item) => (
                    <div key={item.label}>
                      <span className="employee-mobile-summary-label">{item.label}</span>
                      <span className="employee-mobile-summary-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="employee-mobile-section">
                <h3>Personal Information</h3>
                <div className="employee-mobile-field-list">
                  {personalFields.map((field) => (
                    <div key={field.label} className="employee-mobile-field-item">
                      <span className="employee-mobile-field-label">{field.label}</span>
                      <span className="employee-mobile-field-value">{field.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="employee-mobile-section">
                <h3>Bank &amp; PF Information</h3>
                <div className="employee-mobile-field-list">
                  <div className="employee-mobile-field-item">
                    <span className="employee-mobile-field-label">PF Number</span>
                    <span className="employee-mobile-field-value">{getDisplayValue(profile?.pf?.pfNumber)}</span>
                  </div>
                  <div className="employee-mobile-field-item">
                    <span className="employee-mobile-field-label">UAN Number</span>
                    <span className="employee-mobile-field-value">{getDisplayValue(profile?.pf?.uanNumber)}</span>
                  </div>
                  <div className="employee-mobile-field-item">
                    <span className="employee-mobile-field-label">ESI Number</span>
                    <span className="employee-mobile-field-value">{getDisplayValue(profile?.pf?.esiNumber)}</span>
                  </div>
                  <div className="employee-mobile-field-item">
                    <span className="employee-mobile-field-label">Insurer</span>
                    <span className="employee-mobile-field-value">{getDisplayValue(profile?.pf?.insurerName)}</span>
                  </div>
                  {bankFields.map((field) => (
                    <div key={field.label} className="employee-mobile-field-item">
                      <span className="employee-mobile-field-label">{field.label}</span>
                      <span className="employee-mobile-field-value">{field.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="employee-mobile-section">
                <h3>Employment Information</h3>
                <div className="employee-mobile-field-list">
                  {employmentFields.map((field) => (
                    <div key={field.label} className="employee-mobile-field-item">
                      <span className="employee-mobile-field-label">{field.label}</span>
                      <span className="employee-mobile-field-value">{field.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              {qualificationEntries.length > 0 && (
                <section className="employee-mobile-section">
                  <h3>Education</h3>
                  <div className="employee-mobile-field-list">
                    {qualificationEntries.map((entry) => {
                      const documentField = entry.fields.find((field) =>
                        Object.prototype.hasOwnProperty.call(field, 'documentUrl'),
                      );
                      const infoFields = entry.fields.filter((field) => field !== documentField);

                      return (
                        <div key={entry.id} className="employee-mobile-field-item">
                          <span className="employee-mobile-chip">{entry.title}</span>
                          <div className="employee-mobile-field-list">
                            {infoFields.map((field) => (
                              <div key={field.label} className="employee-mobile-field-item">
                                <span className="employee-mobile-field-label">{field.label}</span>
                                <span className="employee-mobile-field-value">{field.value}</span>
                              </div>
                            ))}
                            <div className="employee-mobile-field-item">
                              <span className="employee-mobile-field-label">
                                {documentField?.label || 'Supporting Document'}
                              </span>
                              {documentField?.documentUrl && documentField.value !== '—' ? (
                                <a
                                  href={documentField.documentUrl}
                                  className="employee-mobile-field-value"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Document
                                </a>
                              ) : (
                                <span className="employee-mobile-field-value">
                                  {documentField?.value || 'No document uploaded'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {familyMembers.length > 0 && (
                <section className="employee-mobile-section">
                  <h3>Family Details</h3>
                  <div className="employee-mobile-family">
                    {familyMembers.map((member) => (
                      <div key={member.id} className="employee-mobile-field-item">
                        <span className="employee-mobile-chip">{formatEnumValue(member.relation)}</span>
                        <span className="employee-mobile-field-value">{getDisplayValue(member.name)}</span>
                        {member.mobile && (
                          <span className="employee-mobile-field-value">{member.mobile}</span>
                        )}
                        {member.dob && (
                          <span className="employee-mobile-field-value">{formatDate(member.dob)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {visibleDocuments.length > 0 && (
                <section className="employee-mobile-section">
                  <h3>Documents</h3>
                  <div className="employee-mobile-documents">
                    {visibleDocuments.map((document) => {
                      const documentUrl = resolveUploadImageUrl(document.filePath);
                      const label = document.documentTypeName || document.documentType;
                      return (
                        <div key={document.id} className="employee-mobile-field-item">
                          <span className="employee-mobile-field-label">{formatEnumValue(label)}</span>
                          {documentUrl ? (
                            <a
                              href={documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="employee-mobile-field-value employee-mobile-link-inline"
                            >
                              {getDisplayValue(document.fileName) || 'View Document'}
                            </a>
                          ) : (
                            <span className="employee-mobile-field-value">{getDisplayValue(document.fileName)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {activeView === 'payslips' && (
          <div className="employee-mobile-view">
            <header className="employee-mobile-view-header">
              <h2>Monthly salary payslips</h2>
              <p>Access the latest three months of salary slips.</p>
            </header>
            <div className="employee-mobile-scroll">
              {payslipLoading ? (
                <div className="employee-mobile-empty">Loading payslips…</div>
              ) : payslipError ? (
                <div className="employee-mobile-empty" role="alert">
                  <p>{payslipError}</p>
                  <button
                    type="button"
                    className="employee-mobile-primary"
                    onClick={() => setActiveView('dashboard')}
                  >
                    Back
                  </button>
                </div>
              ) : payslips.length === 0 ? (
                <div className="employee-mobile-empty">No payslip information available.</div>
              ) : (
                <div className="employee-mobile-payslip-list">
                  {payslipDownloadError && (
                    <p className="employee-mobile-alert error">{payslipDownloadError}</p>
                  )}
                  {payslips.map((payslip) => (
                    <article key={payslip.id} className="employee-mobile-payslip-card">
                      <div className="employee-mobile-payslip-header">
                        <h3>{resolvePayslipLabel(payslip)}</h3>
                        <span className="employee-mobile-pill">{formatShortDate(payslip.paymentDate) || '—'}</span>
                      </div>
                      <div className="employee-mobile-payslip-body">
                        <div>
                          <span className="label">Net pay</span>
                          <span className="value">{formatAmount(payslip.netPay)}</span>
                        </div>
                        <button
                          type="button"
                          className="employee-mobile-icon-button"
                          aria-label="Download payslip"
                          onClick={() => handleDownloadPayslip(payslip)}
                          disabled={payslipDownloadId === payslip.id}
                        >
                          <FiDownload />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeView === 'notifications' && (
          <div className="employee-mobile-view">
            <header className="employee-mobile-view-header">
              <h2>Notifications</h2>
              <p>Stay updated with HR and outlet announcements.</p>
            </header>
            <div className="employee-mobile-scroll">
              {notificationLoading ? (
                <div className="employee-mobile-empty">Loading notifications…</div>
              ) : notificationError ? (
                <div className="employee-mobile-empty" role="alert">
                  <p>{notificationError}</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="employee-mobile-empty">No notifications available.</div>
              ) : (
                <ul className="employee-mobile-notification-list">
                  {sortedNotifications.map((notification) => (
                    <li key={notification.id}>
                      <div className="employee-mobile-notification-header">
                        <span className="employee-mobile-notification-title">
                          {resolveNotificationTitle(notification)}
                        </span>
                        <span className="employee-mobile-notification-time">
                          {formatDateTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="employee-mobile-notification-message">
                        {resolveNotificationDescription(notification)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}



        {activeView === 'enquiries' && (
          <div className="employee-mobile-view">
            <header className="employee-mobile-view-header">
              <h2>Send an enquiry</h2>
              <p>Let HR know if you have any questions or need assistance.</p>
            </header>
            <div className="employee-mobile-scroll">
              <form className="employee-mobile-form" onSubmit={handleEnquirySubmit}>
                <div className="employee-mobile-field">
                  <label htmlFor="enquiry-subject">Subject (optional)</label>
                  <input
                    id="enquiry-subject"
                    name="subject"
                    type="text"
                    value={enquiryForm.subject}
                    onChange={handleEnquiryChange}
                    placeholder="Eg. Clarification on payslip"
                  />
                </div>
                <div className="employee-mobile-field">
                  <label htmlFor="enquiry-message">How can we help?</label>
                  <textarea
                    id="enquiry-message"
                    name="message"
                    value={enquiryForm.message}
                    onChange={handleEnquiryChange}
                    placeholder="Describe your question or request"
                    rows={4}
                    required
                  />
                </div>
                {enquiryStatus.error && <p className="employee-mobile-alert error">{enquiryStatus.error}</p>}
                {enquiryStatus.success && <p className="employee-mobile-alert success">{enquiryStatus.success}</p>}
                <button type="submit" className="employee-mobile-primary" disabled={enquiryStatus.submitting}>
                  {enquiryStatus.submitting ? 'Sending…' : 'Submit enquiry'}
                </button>
              </form>

              {enquiries.length > 0 && (
                <section className="employee-mobile-section">
                  <h3>Recent enquiries</h3>
                  <ul className="employee-mobile-enquiry-list">
                    {enquiries.map((item) => (
                      <li key={item.id}>
                        <div>
                          <span className="employee-mobile-enquiry-message">{item.message}</span>
                          <span className="employee-mobile-enquiry-meta">{formatDateTime(item.createdAt)}</span>
                        </div>
                        <span className={`employee-mobile-enquiry-status status-${item.status || 'pending'}`}>
                          {formatEnumValue(item.status)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeMobileDetails;
